import React, { useEffect, useState } from "react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { useLanguage } from "@/context/LanguageContext";
import { Toaster } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { ShieldCheck, FileText, Scale, Cookie, Lock, Sparkles, Download } from "lucide-react";

const SECTIONS = [
  { id: "mentions-legales", Icon: FileText, key: "mentions" },
  { id: "confidentialite", Icon: Lock, key: "privacy" },
  { id: "cgv", Icon: Scale, key: "cgv" },
  { id: "cgu", Icon: FileText, key: "cgu" },
  { id: "cookies", Icon: Cookie, key: "cookies" },
  { id: "ia-charter", Icon: Sparkles, key: "ai" },
  { id: "ia-high-risk", Icon: ShieldCheck, key: "highrisk" },
];

const Legal = () => {
  const { lang } = useLanguage();
  const [active, setActive] = useState("mentions-legales");

  useEffect(() => {
    // Sync with URL hash
    const hash = window.location.hash.replace("#", "");
    if (hash && SECTIONS.find((s) => s.id === hash)) setActive(hash);

    // Enable print mode for this page only
    document.body.classList.add("printable");
    document.documentElement.classList.add("printable-mode");
    return () => {
      document.body.classList.remove("printable");
      document.documentElement.classList.remove("printable-mode");
    };
  }, []);

  const handleDownloadPdf = () => {
    // Trigger browser print dialog — user selects "Save as PDF"
    const originalTitle = document.title;
    const stamp = new Date().toISOString().slice(0, 10);
    document.title = `BICENTER-AI_Conformite_${stamp}`;
    window.print();
    setTimeout(() => {
      document.title = originalTitle;
    }, 500);
  };

  const T = TEXTS[lang] || TEXTS.fr;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white antialiased" data-testid="legal-page">
      <SEO />
      <Header />

      {/* Print-only cover page */}
      <div className="print-cover">
        <h1 style={{ fontSize: "28px", marginBottom: "12px" }}>BICENTER AI</h1>
        <p style={{ fontSize: "16px", marginBottom: "40px" }}>Mentions légales, RGPD & Conformité</p>
        <p style={{ fontSize: "12px", color: "#555" }}>www.bicenter-ai.com — Édité le {new Date().toLocaleDateString("fr-FR")}</p>
        <p style={{ fontSize: "12px", color: "#555", marginTop: "60px" }}>Document officiel conforme aux textes applicables au 13 août 2026 : RGPD (UE 2016/679), Data Act (UE 2023/2854), Loi Informatique et Libertés modifiée, lignes directrices CNIL 2025-2026 (dark patterns, IA, cookies), Digital Services Act (UE 2022/2065), Digital Markets Act (UE 2022/1925), AI Act (UE 2024/1689 — Chapitre III applicable depuis le 2 août 2026 aux systèmes d'IA à haut risque), Loi SREN du 21 mai 2024, Code de la consommation français (Art. L.216, L.221, L.224-25), Loi Chatel et Loi Hamon.</p>
      </div>

      <main className="pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="mb-12 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <p className="text-xs uppercase tracking-[0.4em] text-bicenter-cyan font-mono">
                {T.kicker}
              </p>
              <h1 className="mt-3 font-display text-4xl md:text-5xl lg:text-6xl font-light tracking-tight text-white leading-[1.05]">
                {T.title}
              </h1>
              <p className="mt-6 text-zinc-400 max-w-3xl leading-relaxed">{T.intro}</p>
              <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 bg-white/[0.03] text-xs text-zinc-300">
                <ShieldCheck size={12} className="text-bicenter-cyan" />
                {T.last_update}
              </div>
            </div>
            <Button
              onClick={handleDownloadPdf}
              data-testid="legal-print-button"
              className="bg-gradient-to-r from-bicenter-cyan to-bicenter-blue text-white h-12 px-6 hover:shadow-[0_0_30px_rgba(0,169,255,0.4)] shrink-0"
            >
              <Download size={16} className="mr-2" />
              {T.download_pdf}
            </Button>
          </div>

          <div className="grid lg:grid-cols-12 gap-10">
            {/* Sidebar nav */}
            <aside className="lg:col-span-3 lg:sticky lg:top-32 lg:self-start" data-testid="legal-sidebar">
              <nav className="border border-white/10 rounded-2xl p-3 bg-[#0c0c0e]">
                {SECTIONS.map((s) => {
                  const Icon = s.Icon;
                  return (
                    <a
                      key={s.id}
                      href={`#${s.id}`}
                      onClick={() => setActive(s.id)}
                      data-testid={`legal-nav-${s.id}`}
                      className={`flex items-center gap-3 px-3 py-3 rounded-xl text-sm transition-all ${
                        active === s.id
                          ? "bg-white/5 text-white border border-white/10"
                          : "text-zinc-400 hover:text-white hover:bg-white/[0.03]"
                      }`}
                    >
                      <Icon size={14} className={active === s.id ? "text-bicenter-cyan" : ""} />
                      <span>{T.sections[s.key].nav}</span>
                    </a>
                  );
                })}
              </nav>
            </aside>

            {/* Content */}
            <article className="lg:col-span-9 space-y-16 selectable" data-selectable="true">
              {SECTIONS.map((s) => (
                <section key={s.id} id={s.id} className="scroll-mt-32" data-testid={`legal-section-${s.id}`}>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-bicenter-cyan/10 to-bicenter-violet/10 border border-white/10 flex items-center justify-center">
                      <s.Icon size={16} className="text-white" />
                    </div>
                    <h2 className="font-display text-2xl md:text-3xl text-white font-medium">
                      {T.sections[s.key].title}
                    </h2>
                  </div>
                  <div className="prose prose-invert max-w-none text-zinc-300 leading-relaxed space-y-4 [&_h3]:font-display [&_h3]:text-white [&_h3]:text-lg [&_h3]:font-medium [&_h3]:mt-8 [&_h3]:mb-3 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-1.5 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:space-y-1.5 [&_p]:text-sm [&_p]:text-zinc-300 [&_li]:text-sm [&_a]:text-bicenter-cyan [&_a]:underline [&_a:hover]:text-white [&_strong]:text-white">
                    {T.sections[s.key].content}
                  </div>
                </section>
              ))}
            </article>
          </div>
        </div>
      </main>

      <Footer />
      <Toaster theme="dark" position="bottom-right" />
    </div>
  );
};

// =============================================================================
// LEGAL CONTENT — FR & EN
// Placeholders [À COMPLÉTER] to be filled with actual company registration data.
// Structure inspired by 2026 EU compliance frameworks (RGPD/GDPR, DSA, AI Act,
// Loi Chatel, Loi Hamon, Code de la consommation Art. L.216 & L.221).
// =============================================================================

const TEXTS = {
  fr: {
    kicker: "Informations légales",
    title: "Mentions légales, RGPD & Conformité",
    intro:
      "BICENTER AI est une marque commercialisée par BICENTER CONSULTING. Cette page centralise l'ensemble des obligations légales, informations RGPD et conditions applicables à l'utilisation du site www.bicenter-ai.com et à la souscription à nos solutions numériques intelligentes.",
    last_update: "Dernière mise à jour : 13 août 2026",
    download_pdf: "Télécharger PDF",
    sections: {
      mentions: {
        nav: "Mentions légales",
        title: "Mentions légales",
        content: (
          <>
            <h3>Éditeur du site</h3>
            <p>
              Le site <strong>www.bicenter-ai.com</strong> est édité par <strong>BICENTER AI</strong>. <strong>BICENTER CONSULTING</strong> commercialise les solutions numériques intelligentes de la marque BICENTER AI.
            </p>
            <ul>
              <li><strong>Raison sociale :</strong> BICENTER AI</li>
              <li><strong>Forme juridique :</strong> EURL</li>
              <li><strong>Capital social :</strong> 219 €</li>
              <li><strong>Siège social :</strong> 60 rue François 1ᵉʳ, 75008 Paris 8, France</li>
              <li><strong>SIREN :</strong> 102 429 297</li>
              <li><strong>SIRET :</strong> 102 429 297 00017</li>
              <li><strong>RCS :</strong> Paris</li>
              <li><strong>N° TVA intracommunautaire :</strong> FR51 102 429 297</li>
              <li><strong>Email :</strong> <a href="mailto:connect@bicenter-ai.com">connect@bicenter-ai.com</a></li>
            </ul>

            <h3>Directeur de la publication</h3>
            <p>Laurianne Ngounou, Dirigeante de BICENTER AI.</p>

            <h3>Hébergement</h3>
            <p>
              Le site est hébergé par <strong>Hostinger International Ltd.</strong>, 61 Lordou Vironos Street, 6023 Larnaca, Chypre — <a href="https://www.hostinger.com" target="_blank" rel="noreferrer">www.hostinger.com</a>.
            </p>

            <h3>Propriété intellectuelle</h3>
            <p>
              L'ensemble des contenus du site www.bicenter-ai.com (textes, images, logos, code, interfaces, structures, illustrations, mots-clés, méthodologies, marques associées) est la propriété exclusive de BICENTER AI ou de ses ayants droit. Toute reproduction, représentation, modification, publication, adaptation, extraction, réutilisation, copie totale ou partielle des éléments du site, quel que soit le moyen ou le procédé utilisé, est <strong>strictement interdite</strong> sans autorisation écrite préalable de BICENTER AI, sous peine de constituer une contrefaçon sanctionnée par les articles L.335-2 et suivants du Code de la propriété intellectuelle.
            </p>

            <h3>Marque BICENTER AI</h3>
            <p>
              BICENTER AI et l'ensemble des solutions BICENTER AI BUSINESS, BICENTER AI AUTOMATION, BICENTER AI CONSULTING, BICENTER AI RH, BICENTER AI eV-CARD et BICENTER AI SPEAKER sont des marques de BICENTER AI. Toute utilisation non autorisée est prohibée.
            </p>

            <h3>Litiges & droit applicable</h3>
            <p>
              Le site et son contenu sont régis par le droit français. En cas de litige, et à défaut de résolution amiable, les tribunaux français seront seuls compétents. Pour les consommateurs, la médiation de la consommation peut être saisie via <a href="https://www.economie.gouv.fr/mediation-conso" target="_blank" rel="noreferrer">economie.gouv.fr/mediation-conso</a>.
            </p>
          </>
        ),
      },
      privacy: {
        nav: "Confidentialité (RGPD)",
        title: "Politique de confidentialité — RGPD",
        content: (
          <>
            <p>
              BICENTER AI attache une importance capitale à la protection des données personnelles des utilisateurs de <strong>www.bicenter-ai.com</strong>. La présente politique est conforme au <strong>Règlement (UE) 2016/679 (RGPD)</strong>, au <strong>Data Act (UE 2023/2854)</strong> applicable depuis le 12 septembre 2025, à la <strong>Loi Informatique et Libertés</strong> modifiée, aux <strong>lignes directrices CNIL 2025-2026</strong> (dark patterns, cookies, IA générative), à la <strong>Loi SREN du 21 mai 2024</strong> et aux dispositions du <strong>Digital Services Act (UE 2022/2065)</strong> et du <strong>Digital Markets Act (UE 2022/1925)</strong>.
            </p>

            <h3>1. Responsable du traitement</h3>
            <p>BICENTER AI — <a href="mailto:connect@bicenter-ai.com">connect@bicenter-ai.com</a>.</p>

            <h3>2. Données collectées</h3>
            <ul>
              <li><strong>Formulaires de contact et de démonstration :</strong> nom, prénom, email professionnel, téléphone, entreprise, fonction, message, solution BICENTER AI souhaitée.</li>
              <li><strong>Inscription aux événements :</strong> nom, email, entreprise, fonction, événement choisi, niveau d'accès (Gratuit / Premium / VIP).</li>
              <li><strong>Candidatures partenaire / sponsor / investisseur :</strong> nom, email, entreprise, fonction, site web, budget indicatif, message.</li>
              <li><strong>Données techniques :</strong> adresse IP, type de navigateur, pages consultées, durée de visite (via cookies analytiques, avec consentement).</li>
            </ul>

            <h3>3. Finalités du traitement</h3>
            <ol>
              <li>Répondre aux demandes de démonstration et de contact.</li>
              <li>Gérer les inscriptions aux événements BICENTER AI.</li>
              <li>Traiter les candidatures partenaires, sponsors et investisseurs.</li>
              <li>Adresser des communications commerciales ciblées (opt-in uniquement).</li>
              <li>Améliorer nos services, mesurer l'audience et détecter les fraudes.</li>
              <li>Respecter nos obligations légales et comptables.</li>
            </ol>

            <h3>4. Bases légales</h3>
            <ul>
              <li><strong>Consentement</strong> (art. 6.1.a RGPD) — pour les cookies non essentiels et la prospection commerciale.</li>
              <li><strong>Exécution du contrat</strong> (art. 6.1.b) — pour la gestion des abonnements et des livrables.</li>
              <li><strong>Intérêt légitime</strong> (art. 6.1.f) — pour la sécurité du site et l'amélioration de nos services.</li>
              <li><strong>Obligation légale</strong> (art. 6.1.c) — pour la conservation comptable et fiscale.</li>
            </ul>

            <h3>5. Durée de conservation</h3>
            <ul>
              <li>Leads non convertis : 3 ans à compter du dernier contact.</li>
              <li>Clients actifs : durée du contrat + 5 ans (obligations comptables).</li>
              <li>Données de facturation : 10 ans.</li>
              <li>Logs techniques : 12 mois maximum.</li>
              <li>Cookies : 13 mois maximum.</li>
            </ul>

            <h3>6. Destinataires</h3>
            <p>
              Vos données sont accessibles uniquement aux personnes habilitées de BICENTER AI et à nos sous-traitants strictement encadrés par contrat conforme à l'article 28 du RGPD (hébergeur, service email transactionnel, outils d'analytics avec consentement). Aucune donnée n'est vendue à des tiers.
            </p>

            <h3>7. Transferts hors UE</h3>
            <p>
              Si un transfert hors Union européenne est nécessaire (par exemple pour un outil basé aux États-Unis), il est encadré par les <strong>Clauses Contractuelles Types</strong> approuvées par la Commission européenne ou une décision d'adéquation (par exemple <em>EU-US Data Privacy Framework</em>).
            </p>

            <h3>8. Vos droits</h3>
            <p>
              Conformément aux articles 15 à 22 du RGPD, vous disposez des droits suivants :
            </p>
            <ul>
              <li>Droit d'accès et de portabilité de vos données</li>
              <li>Droit de rectification</li>
              <li>Droit à l'effacement (« droit à l'oubli »)</li>
              <li>Droit à la limitation du traitement</li>
              <li>Droit d'opposition, notamment pour la prospection</li>
              <li>Droit de retirer votre consentement à tout moment</li>
              <li>Droit de définir des directives post-mortem</li>
              <li>Droit d'introduire une réclamation auprès de la <a href="https://www.cnil.fr" target="_blank" rel="noreferrer">CNIL</a></li>
            </ul>
            <p>
              Pour exercer vos droits : <a href="mailto:connect@bicenter-ai.com">connect@bicenter-ai.com</a> — nous répondons sous 30 jours.
            </p>

            <h3>9. Sécurité</h3>
            <p>
              Nos infrastructures utilisent le chiffrement TLS 1.3, la journalisation des accès, la ségrégation des environnements et des audits de sécurité réguliers. Aucun mot de passe n'est stocké en clair.
            </p>
          </>
        ),
      },
      cgv: {
        nav: "CGV",
        title: "Conditions Générales de Vente (CGV)",
        content: (
          <>
            <p>
              Les présentes Conditions Générales de Vente régissent la commercialisation par BICENTER CONSULTING des solutions numériques de la marque <strong>BICENTER AI</strong> (BICENTER AI BUSINESS, BICENTER AI AUTOMATION, BICENTER AI CONSULTING, BICENTER AI RH, BICENTER AI eV-CARD, BICENTER AI SPEAKER), fournies sous forme d'<strong>abonnement à des services numériques</strong> au sens des articles L.224-25-1 et suivants du Code de la consommation (issus de l'ordonnance du 22 septembre 2021 transposant la directive UE 2019/770). Les présentes CGV intègrent également les évolutions issues de la <strong>Loi SREN du 21 mai 2024</strong> et du <strong>Data Act (UE 2023/2854)</strong> applicable depuis le 12 septembre 2025.
            </p>

            <h3>1. Objet</h3>
            <p>
              BICENTER AI est un cabinet de conseil en stratégie qui commercialise, à des professionnels comme à des particuliers, l'accès à ses solutions numériques intelligentes sur abonnement mensuel ou annuel. Les présentes CGV s'appliquent à toute souscription réalisée sur www.bicenter-ai.com ou sur les sous-domaines des plateformes BICENTER AI.
            </p>

            <h3>2. Description des services</h3>
            <p>
              Les caractéristiques principales de chaque solution, les plans tarifaires et les conditions d'utilisation sont détaillés sur les pages tarifaires des plateformes BICENTER AI. BICENTER AI se réserve le droit de faire évoluer les fonctionnalités des services, sans altération substantielle des droits du client.
            </p>

            <h3>3. Prix et paiement</h3>
            <ul>
              <li>Les prix sont indiqués en euros hors taxes (HT) et toutes taxes comprises (TTC).</li>
              <li>Le paiement s'effectue par carte bancaire ou tout autre moyen accepté sur les plateformes BICENTER AI, via un prestataire de paiement certifié PCI-DSS.</li>
              <li>Les abonnements sont facturés d'avance à la date de souscription puis à chaque échéance.</li>
              <li>En cas de défaut de paiement, l'accès aux services peut être suspendu ou résilié, sans préjudice de tout autre recours.</li>
            </ul>

            <h3>4. Reconduction tacite (Loi Chatel)</h3>
            <p>
              Conformément à l'article L.215-1 du Code de la consommation, les abonnements des consommateurs sont reconduits tacitement à leur échéance. BICENTER AI informe le consommateur de sa faculté de non-reconduction, par écrit ou email, au plus tôt <strong>trois mois</strong> et au plus tard <strong>un mois</strong> avant l'échéance. Le consommateur peut mettre gratuitement fin à la reconduction à tout moment à compter de la date de reconduction.
            </p>

            <h3>5. Droit de rétractation (Loi Hamon)</h3>
            <p>
              Conformément aux articles L.221-18 et suivants du Code de la consommation, le <strong>consommateur</strong> dispose d'un délai de <strong>quatorze (14) jours calendaires</strong> à compter de la souscription pour se rétracter, sans avoir à justifier de motif ni à payer de pénalités.
            </p>
            <p>
              <strong>Exception importante :</strong> conformément à l'article L.221-28, 13° du Code de la consommation, le client renonce expressément à son droit de rétractation lorsqu'il demande le commencement de la prestation numérique avant la fin du délai de 14 jours et qu'il en a été informé au préalable.
            </p>
            <p>
              Pour exercer votre droit : email à <a href="mailto:connect@bicenter-ai.com">connect@bicenter-ai.com</a> ou formulaire de contact.
            </p>

            <h3>6. Résiliation</h3>
            <ul>
              <li>Le client peut résilier son abonnement à tout moment depuis son espace client ou par email à <a href="mailto:connect@bicenter-ai.com">connect@bicenter-ai.com</a>.</li>
              <li>La résiliation prend effet à la fin de la période payée en cours ; aucun remboursement au prorata n'est dû sauf disposition légale contraire.</li>
              <li>BICENTER AI se réserve le droit de résilier un abonnement en cas de manquement grave du client aux présentes CGV, moyennant mise en demeure préalable.</li>
            </ul>

            <h3>7. Livrables & résultats</h3>
            <p>
              Les livrables générés via les solutions BICENTER AI (business models, contrats, fiches candidats, analyses) sont la propriété du client et peuvent être exportés librement. BICENTER AI est tenu à une <strong>obligation de moyens</strong> et non de résultat sur la qualité des livrables générés par l'IA.
            </p>

            <h3>8. Garantie de conformité</h3>
            <p>
              Conformément aux articles L.224-25-1 et suivants du Code de la consommation, BICENTER AI garantit la conformité des services numériques fournis. En cas de défaut de conformité, le consommateur peut demander la mise en conformité sans frais, une réduction du prix ou la résolution du contrat, dans les conditions prévues par la loi.
            </p>

            <h3>9. Responsabilité</h3>
            <p>
              La responsabilité de BICENTER AI est limitée au montant total facturé au client au cours des <strong>douze (12) derniers mois</strong>. BICENTER AI ne pourra être tenu responsable des dommages indirects (perte de chiffre d'affaires, perte de clientèle, préjudice d'image), sauf en cas de faute lourde ou dolosive. Aucune limitation ne s'applique en cas de manquement à une obligation essentielle ou de dommage corporel.
            </p>

            <h3>10. Force majeure</h3>
            <p>
              La responsabilité des parties ne saurait être engagée en cas de force majeure telle que définie par l'article 1218 du Code civil.
            </p>

            <h3>11. Ventes internationales</h3>
            <p>
              Pour les clients situés hors de France :
            </p>
            <ul>
              <li><strong>Union européenne :</strong> TVA facturée selon les règles de l'auto-liquidation (B2B) ou du guichet unique OSS (B2C).</li>
              <li><strong>Hors UE :</strong> facturation hors taxes ; le client est responsable de toute taxe locale applicable.</li>
              <li>Les CGV sont régies par le droit français ; le client consommateur bénéficie néanmoins des dispositions impératives de son pays de résidence habituelle.</li>
            </ul>

            <h3>12. Médiation & litiges</h3>
            <p>
              En cas de litige, le client peut recourir gratuitement à un médiateur de la consommation. BICENTER AI propose la plateforme européenne de règlement en ligne des litiges (RLL) : <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noreferrer">ec.europa.eu/consumers/odr</a>. À défaut de résolution amiable, les tribunaux français seront compétents.
            </p>
          </>
        ),
      },
      cgu: {
        nav: "CGU",
        title: "Conditions Générales d'Utilisation (CGU)",
        content: (
          <>
            <h3>1. Acceptation</h3>
            <p>
              L'accès au site www.bicenter-ai.com implique l'acceptation sans réserve des présentes CGU par tout utilisateur. Ces conditions s'appliquent conformément à la <strong>Loi SREN du 21 mai 2024</strong> (sécuriser et réguler l'espace numérique) et au <strong>Digital Services Act (UE 2022/2065)</strong>.
            </p>
            <h3>2. Usage du site</h3>
            <p>L'utilisateur s'engage à :</p>
            <ul>
              <li>Ne pas utiliser le site à des fins illégales ou contraires à l'ordre public.</li>
              <li>Ne pas tenter d'accéder aux ressources non publiques (dashboards, API admin, bases de données).</li>
              <li>Ne pas scraper, extraire ni reproduire de manière automatisée les contenus du site (art. L.342-1 CPI, protection <em>sui generis</em> des bases de données).</li>
              <li><strong>Ne pas utiliser les contenus du site pour entraîner des modèles d'intelligence artificielle sans autorisation écrite préalable.</strong> BICENTER AI exerce ici son droit d'opt-out à la fouille de textes et données (TDM opt-out) prévu à l'article L.122-5-3 du Code de la propriété intellectuelle et à l'article 4 de la directive (UE) 2019/790.</li>
              <li>Respecter les droits de propriété intellectuelle de BICENTER AI.</li>
            </ul>

            <h3>3. Comportements interdits (Loi SREN + DSA)</h3>
            <ul>
              <li>Toute forme de piratage, injection, déni de service ou intrusion.</li>
              <li>La création de faux profils, l'usurpation d'identité ou l'utilisation de deepfakes.</li>
              <li>La diffusion de contenus illégaux, diffamatoires, discriminatoires, haineux, à caractère terroriste ou pédopornographique (signalement possible via <a href="https://www.internet-signalement.gouv.fr" target="_blank" rel="noreferrer">internet-signalement.gouv.fr</a>).</li>
              <li>La manipulation par « dark patterns » telle qu'interdite par l'art. 25 DSA et les lignes directrices CNIL 2024.</li>
            </ul>

            <h3>4. Suspension</h3>
            <p>
              BICENTER AI se réserve le droit de suspendre ou de résilier l'accès de tout utilisateur ne respectant pas les CGU, après mise en demeure sauf en cas d'urgence sécuritaire (art. 23 DSA).
            </p>

            <h3>5. Disponibilité & cybersécurité</h3>
            <p>
              Le site est accessible 24h/24 et 7j/7 sauf maintenance planifiée ou cas de force majeure. BICENTER AI met en œuvre les mesures de cybersécurité conformes à la directive <strong>NIS2 (UE 2022/2555)</strong> transposée en droit français par l'ordonnance du 24 octobre 2024 et au <strong>Cyber Resilience Act (UE 2024/2847)</strong>.
            </p>

            <h3>6. Signalement de contenu illicite</h3>
            <p>
              Tout utilisateur peut signaler un contenu qu'il estime illicite via <a href="mailto:connect@bicenter-ai.com">connect@bicenter-ai.com</a>. Conformément à l'art. 16 DSA, BICENTER AI accuse réception sans délai et statue avec diligence, sans discrimination et de manière motivée.
            </p>

            <h3>7. Liens externes</h3>
            <p>
              Le site peut contenir des liens vers des sites tiers dont BICENTER AI ne maîtrise pas les contenus et décline toute responsabilité.
            </p>
          </>
        ),
      },
      cookies: {
        nav: "Cookies",
        title: "Politique de cookies",
        content: (
          <>
            <p>
              Nous utilisons des cookies conformément à la directive ePrivacy (2002/58/CE modifiée), à l'article 82 de la Loi Informatique et Libertés et aux <strong>lignes directrices CNIL du 15 juillet 2025</strong> sur les traceurs et les <em>dark patterns</em>. Aucun cookie non essentiel n'est déposé sans votre consentement explicite, libre, spécifique, éclairé et univoque.
            </p>

            <h3>1. Cookies strictement nécessaires</h3>
            <p>
              Ces cookies sont indispensables au fonctionnement du site (préférence de langue, consentement, session sécurisée). Ils ne peuvent pas être désactivés.
            </p>

            <h3>2. Cookies de mesure d'audience</h3>
            <p>
              Utilisés avec votre consentement pour comprendre la performance du site (pages consultées, sources de trafic). Anonymisation IP et durée limitée à 13 mois.
            </p>

            <h3>3. Cookies de personnalisation</h3>
            <p>
              Utilisés uniquement avec votre consentement pour proposer un contenu adapté à vos intérêts.
            </p>

            <h3>4. Gestion du consentement</h3>
            <p>
              Vous pouvez modifier vos préférences à tout moment via la bannière de consentement ou en contactant <a href="mailto:connect@bicenter-ai.com">connect@bicenter-ai.com</a>. La preuve du consentement est conservée pendant 6 mois.
            </p>

            <h3>5. Cookies tiers</h3>
            <p>
              Certains outils tiers (analytics, sécurité, prestataire email) peuvent déposer leurs propres cookies. Ils sont listés dans la bannière et soumis à leur propre politique.
            </p>
          </>
        ),
      },
      ai: {
        nav: "Charte IA",
        title: "Charte de transparence IA (AI Act)",
        content: (
          <>
            <p>
              Conformément au <strong>Règlement (UE) 2024/1689 (AI Act)</strong>, dont le <strong>Chapitre III (systèmes d'IA à haut risque) est applicable depuis le 2 août 2026</strong> et le <strong>Chapitre V (modèles d'IA à usage général) depuis le 2 août 2025</strong>, BICENTER AI s'engage à respecter les principes suivants concernant l'usage de l'intelligence artificielle dans ses solutions :
            </p>

            <h3>1. Classification des systèmes d'IA</h3>
            <p>
              Les solutions BICENTER AI relèvent principalement de la catégorie « risque limité » de l'AI Act (art. 50) : elles interagissent directement avec les utilisateurs sans se substituer à une décision juridique, financière ou RH contraignante. <strong>BICENTER AI RH</strong>, en particulier, est une plateforme de mise en relation candidat-professionnel qui n'effectue ni recrutement ni filtrage substantiel automatisé. Lorsqu'un module est déployé par un client dans un contexte à haut risque (notation de crédit, filtrage substantiel de candidats), les obligations du Chapitre III s'appliquent : gestion des risques, gouvernance des données, documentation technique, journalisation, supervision humaine, robustesse et cybersécurité, marquage CE.
            </p>

            <h3>2. Transparence renforcée (art. 50 AI Act)</h3>
            <ul>
              <li>Toute interaction avec un système d'IA est signalée à l'utilisateur.</li>
              <li>Les livrables générés par IA (business models, contrats, fiches, analyses, contenus) sont clairement identifiés comme tels, avec un marquage exploitable par machine (watermarking) lorsque techniquement possible.</li>
              <li>La finalité, les limites et les principales données d'entraînement (au niveau général) des modèles utilisés sont documentées et consultables sur demande.</li>
            </ul>

            <h3>3. Supervision humaine (art. 14 AI Act)</h3>
            <p>
              Aucune décision juridique, financière ou RH contraignante n'est prise de manière entièrement automatisée sans supervision humaine explicite. Le client reste responsable des choix effectués sur la base des recommandations IA. Un bouton d'arrêt (« kill switch ») est disponible pour interrompre à tout moment une opération d'IA sensible.
            </p>

            <h3>4. Gouvernance des données & non-entraînement</h3>
            <p>
              <strong>Les données de nos clients ne sont jamais utilisées pour entraîner ou fine-tuner des modèles d'IA de tiers.</strong> Elles ne sont utilisées que pour l'exécution du service souscrit, conformément à l'article 10 de l'AI Act (qualité et gouvernance des données) et à la <strong>recommandation CNIL du 7 novembre 2025</strong> sur l'IA générative.
            </p>

            <h3>5. Robustesse, cybersécurité & non-discrimination</h3>
            <p>
              Nos modèles sont audités périodiquement pour prévenir les biais discriminatoires et garantir un fonctionnement fiable. Les mesures de cybersécurité intègrent les exigences du <strong>Cyber Resilience Act (UE 2024/2847)</strong> et de la <strong>directive NIS2</strong> transposée en droit français (ordonnance du 24 octobre 2024). Le client peut nous signaler tout incident via <a href="mailto:connect@bicenter-ai.com">connect@bicenter-ai.com</a>.
            </p>

            <h3>6. Droit à l'explication (art. 22 RGPD + art. 86 AI Act)</h3>
            <p>
              Sur demande, nous fournissons une explication compréhensible et significative du fonctionnement des recommandations IA concernant le demandeur, ainsi que la possibilité de contester la décision et d'obtenir une intervention humaine.
            </p>

            <h3>7. Registre de conformité IA</h3>
            <p>
              BICENTER AI tient un registre interne de conformité IA (art. 71 AI Act) documentant : les systèmes d'IA déployés, leur classification de risque, les évaluations de conformité, les incidents éventuels et les mesures correctives. Ce registre est mis à disposition des autorités compétentes (CNIL, Autorité française de l'IA lorsque désignée) sur demande motivée.
            </p>
          </>
        ),
      },
      highrisk: {
        nav: "AI Act — Haut risque",
        title: "Notice AI Act — Systèmes d'IA à haut risque",
        content: (
          <>
            <p>
              En application du <strong>Règlement (UE) 2024/1689 (AI Act)</strong>, dont le Chapitre III est applicable depuis le <strong>2 août 2026</strong>, la présente notice détaille les mesures de conformité mises en œuvre par BICENTER AI pour les solutions susceptibles d'être qualifiées de <strong>systèmes d'IA à haut risque</strong> au sens de l'Annexe III de l'AI Act.
            </p>

            <h3>1. Solutions concernées</h3>
            <p>
              <strong>BICENTER AI RH est une plateforme de mise en relation</strong> entre candidats et professionnels (marketplace de talents). Elle <em>ne procède pas</em> à des décisions de recrutement, ni à un filtrage substantiel automatisé des candidatures, ni à une évaluation contraignante des candidats. À ce titre, elle relève du régime « <strong>risque limité</strong> » de l'AI Act (art. 50) et non du régime haut risque de l'Annexe III §4.a.
            </p>
            <p>
              Une solution BICENTER AI ne bascule dans le régime haut risque que si elle est configurée par le déployeur pour l'un des usages listés à l'Annexe III de l'AI Act :
            </p>
            <ul>
              <li><strong>BICENTER AI RH — hypothèse résiduelle :</strong> si un client-déployeur active des fonctionnalités additionnelles produisant un <em>filtrage substantiel</em> ou une <em>évaluation contraignante</em> des candidats sans intervention humaine réelle, l'usage relèverait de l'Annexe III §4.a. Cette hypothèse est encadrée contractuellement dans les CGV : ces fonctionnalités ne sont activables qu'avec accord exprès de BICENTER AI et mise en conformité préalable.</li>
              <li><strong>BICENTER AI CONSULTING</strong> — uniquement si configurée pour évaluer la solvabilité de personnes physiques ou établir une notation de crédit (Annexe III §5.b), ou l'évaluation du risque en assurance-vie et santé (§5.c).</li>
              <li><strong>BICENTER AI BUSINESS, AUTOMATION, eV-CARD, SPEAKER</strong> — relèvent du régime « risque limité » (art. 50 AI Act) en configuration standard.</li>
            </ul>
            <p>
              <strong>Important :</strong> les fonctionnalités de recommandation générale (business model, rédaction de contrats non contraignants, matching candidat-professionnel indicatif, préparation de fiches sous supervision humaine finale) <em>ne relèvent pas</em> automatiquement du haut risque. La qualification dépend du contexte réel d'utilisation par le client-déployeur.
            </p>

            <h3>2. Système de gestion des risques (art. 9 AI Act)</h3>
            <p>
              Bien que la majorité des solutions BICENTER AI relève du régime « risque limité », BICENTER AI applique par prudence un <strong>processus proportionné de gestion des risques</strong> à l'ensemble de ses solutions et un processus renforcé aux modules pouvant basculer en haut risque :
            </p>
            <ul>
              <li>Identification et analyse des risques prévisibles pour la santé, la sécurité et les droits fondamentaux (notamment risques de biais dans le matching candidat-professionnel de BICENTER AI RH).</li>
              <li>Estimation et évaluation des risques survenant dans le cadre d'un usage conforme à la destination, ainsi que d'un mésusage raisonnablement prévisible (ex. utilisation détournée de la mise en relation à des fins de filtrage automatisé).</li>
              <li>Adoption de mesures de gestion appropriées : supervision humaine, transparence des critères de matching, désactivation par défaut des fonctionnalités susceptibles de basculer en haut risque.</li>
              <li>Tests avant mise sur le marché et surveillance continue post-déploiement, avec audit annuel des algorithmes de matching.</li>
            </ul>

            <h3>3. Gouvernance des données (art. 10 AI Act)</h3>
            <ul>
              <li>Les jeux de données d'entraînement, de validation et de test respectent des critères de qualité, pertinence, représentativité et absence de biais discriminatoires.</li>
              <li>Les données personnelles utilisées sont minimisées et anonymisées lorsque cela est possible.</li>
              <li>Traçabilité complète des jeux de données via un registre interne dédié.</li>
            </ul>

            <h3>4. Documentation technique (art. 11 & Annexe IV AI Act)</h3>
            <p>
              Pour chaque système à haut risque, BICENTER AI établit et tient à jour une documentation technique décrivant :
            </p>
            <ol>
              <li>Description générale du système d'IA et de sa destination.</li>
              <li>Éléments détaillés du système : architecture, algorithmes, ressources computationnelles, jeux de données.</li>
              <li>Processus de développement, méthodes d'entraînement et de test, mesures d'assurance qualité.</li>
              <li>Surveillance, fonctionnement, contrôle et supervision humaine.</li>
              <li>Description des risques résiduels, mesures d'atténuation.</li>
              <li>Modifications significatives apportées au système.</li>
              <li>Liste des normes harmonisées appliquées et déclaration UE de conformité.</li>
            </ol>

            <h3>5. Enregistrement et journalisation (art. 12 AI Act)</h3>
            <p>
              Les systèmes à haut risque enregistrent automatiquement des logs (« journaux ») pertinents permettant : identification de situations à risque, facilitation de la surveillance post-marché et vérification de la performance dans le temps. Les journaux sont conservés <strong>au minimum 6 mois</strong>.
            </p>

            <h3>6. Transparence et information des déployeurs (art. 13 AI Act)</h3>
            <p>
              Chaque système à haut risque est accompagné d'une notice d'utilisation claire, complète et accessible aux clients-déployeurs, indiquant : identité du fournisseur, caractéristiques et limites, niveau de précision, spécifications d'entrée, mesures humaines de contrôle, durée prévue et procédures de maintenance.
            </p>

            <h3>7. Supervision humaine effective (art. 14 AI Act)</h3>
            <ul>
              <li>Interface utilisateur permettant à la personne physique désignée de comprendre les capacités et limites du système.</li>
              <li>Possibilité d'ignorer, d'écraser ou d'annuler la sortie du système.</li>
              <li>Possibilité de stopper le système via un bouton « kill switch » ou une procédure similaire.</li>
              <li>Rester vigilant à l'égard du <em>biais d'automatisation</em>.</li>
            </ul>

            <h3>8. Précision, robustesse et cybersécurité (art. 15 AI Act)</h3>
            <p>
              Niveaux de précision et de robustesse déclarés dans la notice ; résilience aux erreurs, défaillances et tentatives d'exploitation par des tiers non autorisés ; conformité au <strong>Cyber Resilience Act (UE 2024/2847)</strong>.
            </p>

            <h3>9. Évaluation de conformité et marquage CE (art. 43 &amp; suiv.)</h3>
            <p>
              Avant toute mise sur le marché d'un système à haut risque, BICENTER AI effectue la procédure d'évaluation de conformité applicable, appose le <strong>marquage CE</strong> et établit la <strong>déclaration UE de conformité</strong>.
            </p>

            <h3>10. Enregistrement dans la base de données de l'UE (art. 49 &amp; 71)</h3>
            <p>
              Tout système à haut risque déployé par BICENTER AI est enregistré dans la <strong>base de données publique de l'UE pour les systèmes d'IA à haut risque</strong> avant sa mise en service, conformément à l'article 49.
            </p>

            <h3>11. Analyse d'impact sur les droits fondamentaux — FRIA (art. 27 AI Act)</h3>
            <p>
              Lorsque le déployeur est une autorité publique, un organisme public ou une entité privée fournissant des services publics, une <strong>analyse d'impact sur les droits fondamentaux (FRIA)</strong> est requise avant la première utilisation. BICENTER AI fournit à ses déployeurs concernés un modèle de FRIA et l'accompagnement nécessaire.
            </p>

            <h3>12. Surveillance post-marché et notification d'incidents (art. 72 &amp; 73)</h3>
            <ul>
              <li>Système de surveillance active de la performance du système après mise sur le marché.</li>
              <li>Notification à l'autorité de surveillance nationale (en France : autorité désignée par la Loi de mise en œuvre de l'AI Act — probablement CNIL et/ou ARCEP) de tout incident grave ou dysfonctionnement affectant les droits fondamentaux <strong>dans les 15 jours</strong> suivant la prise de connaissance (72h pour les cas graves).</li>
            </ul>

            <h3>13. Coopération avec les autorités</h3>
            <p>
              BICENTER AI coopère pleinement avec la CNIL, la future autorité française chargée de la surveillance de l'AI Act, et le <strong>Bureau européen de l'IA</strong> (AI Office). Toute demande d'accès à la documentation technique est traitée sous <strong>30 jours</strong>.
            </p>

            <h3>14. Sanctions et voies de recours</h3>
            <p>
              Le non-respect des obligations relatives aux systèmes à haut risque peut entraîner des sanctions administratives allant jusqu'à <strong>15 millions d'euros ou 3% du chiffre d'affaires mondial annuel</strong> (art. 99 AI Act), en application par les autorités nationales. Toute personne concernée peut également saisir la CNIL et exercer un recours juridictionnel.
            </p>

            <p style={{ marginTop: "24px", padding: "16px", borderLeft: "3px solid #00A9FF" }}>
              <strong>Contact conformité IA :</strong> <a href="mailto:connect@bicenter-ai.com">connect@bicenter-ai.com</a> — Toute demande relative à la conformité AI Act sera traitée par notre responsable conformité sous 30 jours.
            </p>
          </>
        ),
      },
    },
  },

  // ==== ENGLISH ====
  en: {
    kicker: "Legal information",
    title: "Legal notice, GDPR & Compliance",
    intro:
      "BICENTER AI is a brand commercialized by BICENTER CONSULTING. This page centralizes all legal obligations, GDPR information and terms applicable to www.bicenter-ai.com and to any subscription to our intelligent digital solutions.",
    last_update: "Last update: August 13, 2026",
    download_pdf: "Download PDF",
    sections: {
      mentions: {
        nav: "Legal notice",
        title: "Legal notice",
        content: (
          <>
            <h3>Publisher</h3>
            <p>
              The website <strong>www.bicenter-ai.com</strong> is published by <strong>BICENTER AI</strong>. <strong>BICENTER CONSULTING</strong> commercializes the intelligent digital solutions of the BICENTER AI brand.
            </p>
            <ul>
              <li><strong>Company name:</strong> BICENTER AI</li>
              <li><strong>Legal form:</strong> EURL (single-member limited liability company under French law)</li>
              <li><strong>Share capital:</strong> €219</li>
              <li><strong>Registered address:</strong> 60 rue François 1ᵉʳ, 75008 Paris 8, France</li>
              <li><strong>Registration number (SIREN):</strong> 102 429 297</li>
              <li><strong>Commercial Register:</strong> RCS Paris</li>
              <li><strong>EU VAT number:</strong> [TO BE COMPLETED]</li>
              <li><strong>Email:</strong> <a href="mailto:connect@bicenter-ai.com">connect@bicenter-ai.com</a></li>
            </ul>

            <h3>Publication director</h3>
            <p>Laurianne Ngounou, Director of BICENTER AI.</p>

            <h3>Hosting</h3>
            <p>Website hosted by <strong>Hostinger International Ltd.</strong>, Cyprus.</p>

            <h3>Intellectual property</h3>
            <p>
              All content on www.bicenter-ai.com (texts, images, logos, code, structures, methodologies, trademarks) is the exclusive property of BICENTER AI or its rights-holders. Any reproduction, representation, adaptation, extraction, re-use or copy of the site's elements without prior written consent is <strong>strictly prohibited</strong> and constitutes an infringement sanctioned under French intellectual property law.
            </p>

            <h3>Applicable law</h3>
            <p>The website and its content are governed by French law. French courts have exclusive jurisdiction.</p>
          </>
        ),
      },
      privacy: {
        nav: "Privacy (GDPR)",
        title: "Privacy Policy — GDPR",
        content: (
          <>
            <p>
              This policy complies with <strong>Regulation (EU) 2016/679 (GDPR)</strong>, the French Data Protection Act, CNIL 2024-2026 guidelines and the <strong>Digital Services Act</strong>.
            </p>
            <h3>Data controller</h3>
            <p>BICENTER AI — <a href="mailto:connect@bicenter-ai.com">connect@bicenter-ai.com</a>.</p>
            <h3>Data collected</h3>
            <ul>
              <li>Contact / demo forms: name, professional email, phone, company, role, message, chosen solution.</li>
              <li>Event registrations: name, email, company, role, event, access tier.</li>
              <li>Applications (partner, sponsor, investor): name, email, company, role, website, budget, message.</li>
              <li>Technical data: IP, browser, pages visited (analytics with consent only).</li>
            </ul>
            <h3>Legal bases</h3>
            <ul>
              <li>Consent (Art. 6.1.a) — non-essential cookies, marketing.</li>
              <li>Contract performance (Art. 6.1.b) — subscriptions and deliverables.</li>
              <li>Legitimate interest (Art. 6.1.f) — site security, service improvement.</li>
              <li>Legal obligation (Art. 6.1.c) — accounting/tax retention.</li>
            </ul>
            <h3>Retention</h3>
            <ul>
              <li>Unconverted leads: 3 years.</li>
              <li>Active customers: contract duration + 5 years.</li>
              <li>Billing data: 10 years.</li>
              <li>Technical logs: 12 months max.</li>
              <li>Cookies: 13 months max.</li>
            </ul>
            <h3>Your rights (GDPR Art. 15-22)</h3>
            <ul>
              <li>Access, rectification, erasure, restriction, portability, objection.</li>
              <li>Withdraw consent at any time.</li>
              <li>File a complaint with the CNIL (French data protection authority) or your local supervisory authority.</li>
            </ul>
            <p>Contact for rights: <a href="mailto:connect@bicenter-ai.com">connect@bicenter-ai.com</a> — we respond within 30 days.</p>
          </>
        ),
      },
      cgv: {
        nav: "Terms of Sale",
        title: "General Terms of Sale",
        content: (
          <>
            <p>
              These terms govern the commercialization by BICENTER AI of BICENTER AI digital solutions (BICENTER AI BUSINESS, BICENTER AI AUTOMATION, BICENTER AI CONSULTING, BICENTER AI RH, BICENTER AI eV-CARD, BICENTER AI SPEAKER) provided as <strong>subscription services</strong>.
            </p>
            <h3>Pricing & payment</h3>
            <ul>
              <li>Prices in EUR, VAT excluded and included.</li>
              <li>Payment via PCI-DSS certified provider.</li>
              <li>Subscriptions billed in advance.</li>
            </ul>
            <h3>Right of withdrawal (14 days — EU consumers)</h3>
            <p>
              Consumers have 14 calendar days to withdraw, unless service has expressly begun with prior information (Article L.221-28, 13° French Consumer Code).
            </p>
            <h3>Tacit renewal (Chatel Law)</h3>
            <p>
              Consumer subscriptions are renewed tacitly; the consumer is notified 1–3 months before renewal and may cancel free of charge from the renewal date onwards.
            </p>
            <h3>Cancellation</h3>
            <p>
              Cancel anytime via customer area or email. Effective at the end of the current paid period; no pro-rata refund unless required by law.
            </p>
            <h3>Deliverables</h3>
            <p>
              AI-generated deliverables (business models, contracts, candidate briefs, analyses) belong to the customer and can be exported. BICENTER AI has an <strong>obligation of means</strong>, not results.
            </p>
            <h3>Liability</h3>
            <p>
              Liability capped at the amount paid over the last 12 months. No liability for indirect damages, except gross negligence or willful misconduct.
            </p>
            <h3>International sales</h3>
            <ul>
              <li>EU B2B: reverse-charge VAT.</li>
              <li>EU B2C: OSS scheme.</li>
              <li>Outside EU: net of tax; buyer responsible for local taxes.</li>
            </ul>
          </>
        ),
      },
      cgu: {
        nav: "Terms of Use",
        title: "Terms of Use",
        content: (
          <>
            <h3>Acceptance</h3>
            <p>Access to www.bicenter-ai.com implies unreserved acceptance of these terms.</p>
            <h3>Prohibited conduct</h3>
            <ul>
              <li>No scraping, automated extraction or reuse of site content.</li>
              <li>No unauthorized use of content to train third-party AI models.</li>
              <li>No hacking, injection or denial of service.</li>
              <li>No unlawful, defamatory, discriminatory or pornographic content.</li>
            </ul>
            <h3>Availability</h3>
            <p>Best-effort 24/7 availability. Scheduled maintenance may apply.</p>
          </>
        ),
      },
      cookies: {
        nav: "Cookies",
        title: "Cookie policy",
        content: (
          <>
            <p>Compliant with the ePrivacy Directive and 2024 CNIL guidelines.</p>
            <h3>Strictly necessary cookies</h3>
            <p>Required for site operation (language, consent, secure session). Cannot be disabled.</p>
            <h3>Analytics cookies</h3>
            <p>With consent, anonymized IP, 13-month max retention.</p>
            <h3>Personalization cookies</h3>
            <p>With consent only, to tailor content.</p>
            <h3>Consent management</h3>
            <p>Change your preferences anytime via the banner or by emailing us.</p>
          </>
        ),
      },
      ai: {
        nav: "AI Charter",
        title: "AI Transparency Charter (AI Act)",
        content: (
          <>
            <p>Compliant with <strong>EU Regulation 2024/1689 (AI Act)</strong> — <strong>Chapter III (high-risk AI systems) applicable since August 2, 2026</strong>, and Chapter V (general-purpose AI models) applicable since August 2, 2025.</p>
            <h3>Transparency</h3>
            <ul>
              <li>All AI interactions are disclosed.</li>
              <li>AI-generated deliverables are identified as such.</li>
              <li>Model purpose and limits documented.</li>
            </ul>
            <h3>Human oversight</h3>
            <p>No binding legal or financial decision is made fully automatically. Customer remains responsible.</p>
            <h3>Customer data are not used for training</h3>
            <p><strong>Customer data are never used to train or fine-tune third-party AI models.</strong> They are used only to perform the subscribed service.</p>
            <h3>Right to explanation</h3>
            <p>On request, we provide a plain-language explanation of AI recommendations concerning the requester (GDPR Art. 22).</p>
          </>
        ),
      },
      highrisk: {
        nav: "AI Act — High risk",
        title: "AI Act Notice — High-risk AI systems",
        content: (
          <>
            <p>
              Under <strong>EU Regulation 2024/1689 (AI Act)</strong>, whose Chapter III applies since <strong>2 August 2026</strong>, this notice outlines the compliance measures implemented by BICENTER AI for solutions that may qualify as <strong>high-risk AI systems</strong> per Annex III.
            </p>

            <h3>1. Solutions concerned</h3>
            <p>
              <strong>BICENTER AI RH is a talent matching platform</strong> (marketplace) connecting candidates and professionals. It <em>does not</em> perform recruitment decisions, substantial automated filtering, or binding assessment of candidates. It therefore falls under the <strong>limited-risk regime</strong> (Art. 50 AI Act), not the high-risk regime of Annex III §4.a.
            </p>
            <ul>
              <li><strong>BICENTER AI RH — residual case:</strong> only if a deployer activates additional features producing substantial filtering or binding assessment without genuine human oversight, in which case Annex III §4.a applies. Such features require BICENTER AI's express approval and prior compliance measures under the ToS.</li>
              <li><strong>BICENTER AI CONSULTING</strong> — only when configured for creditworthiness assessment (Annex III §5.b) or life/health insurance risk (§5.c).</li>
              <li><strong>BICENTER AI BUSINESS, AUTOMATION, eV-CARD, SPEAKER</strong> — limited-risk regime (Art. 50) in standard configuration.</li>
            </ul>

            <h3>2. Risk management (Art. 9)</h3>
            <p>
              BICENTER AI applies a proportionate risk management process to all solutions, and a reinforced one to modules that could shift to high-risk: bias detection in matching, transparency of matching criteria, default deactivation of features that could trigger high-risk classification, pre-market testing and annual algorithmic audits.
            </p>

            <h3>3. Data governance (Art. 10)</h3>
            <p>Training / validation / test datasets meet quality, relevance, representativeness and non-discrimination criteria. Full traceability via internal registry.</p>

            <h3>4. Technical documentation (Art. 11 &amp; Annex IV)</h3>
            <p>Documented architecture, algorithms, training methodology, quality assurance, human oversight, residual risks and EU declaration of conformity.</p>

            <h3>5. Logging (Art. 12)</h3>
            <p>Automatic logs kept for at least 6 months to enable post-market monitoring.</p>

            <h3>6. Transparency (Art. 13) &amp; Human oversight (Art. 14)</h3>
            <p>Clear user-facing documentation. Deployer can override, stop or kill the system at any time. Vigilance against automation bias.</p>

            <h3>7. Accuracy, robustness &amp; cybersecurity (Art. 15)</h3>
            <p>Declared accuracy and robustness levels, resilience to attacks, compliance with the <strong>Cyber Resilience Act (EU 2024/2847)</strong>.</p>

            <h3>8. Conformity assessment &amp; CE marking (Art. 43+)</h3>
            <p>Conformity assessment procedure, CE marking and EU declaration of conformity issued before market placement.</p>

            <h3>9. EU database registration (Art. 49 &amp; 71)</h3>
            <p>Every high-risk system is registered in the EU public database for high-risk AI systems before deployment.</p>

            <h3>10. FRIA (Art. 27)</h3>
            <p>A Fundamental Rights Impact Assessment is provided for public-sector deployers before first use.</p>

            <h3>11. Post-market monitoring &amp; incident reporting (Art. 72 &amp; 73)</h3>
            <p>Active monitoring; incident notification to the national supervisory authority within 15 days (72h for serious cases).</p>

            <h3>12. Sanctions (Art. 99)</h3>
            <p>Up to <strong>€15 million or 3% of global annual turnover</strong> for high-risk violations.</p>

            <p style={{ marginTop: "24px", padding: "16px", borderLeft: "3px solid #00A9FF" }}>
              <strong>AI compliance contact:</strong> <a href="mailto:connect@bicenter-ai.com">connect@bicenter-ai.com</a> — replies within 30 days.
            </p>
          </>
        ),
      },
    },
  },
};

export default Legal;
